/**
 * Anomaly Drawer
 * Bottom drawer showing assets with anomalies (low battery, lost connection)
 * Supports CSV export
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  BatteryAlert as BatteryIcon,
  SignalCellularOff as SignalIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';

import { colorPalette } from '../../theme';
import { formatDateTime } from '../../utils/helpers';

const BATTERY_LOW_THRESHOLD = 20;
const CONNECTION_LOST_HOURS = 6;

/**
 * Detect anomalies on a list of assets.
 * Returns { lowBattery: [...], lostConnection: [...] }
 */
export const detectAnomalies = (assets, assetTypes = []) => {
  const now = Date.now();
  const lowBattery = [];
  const lostConnection = [];

  const typeMap = {};
  for (const t of assetTypes) {
    typeMap[t.id] = t;
  }

  for (const asset of assets) {
    // Low battery check
    const battery = asset.battery ?? asset.beaconBattery;
    if (battery !== undefined && battery !== null && battery <= BATTERY_LOW_THRESHOLD) {
      lowBattery.push(asset);
    }

    // Lost connection check
    const receivedTs = asset.receivedTs || asset.eventTs;
    if (receivedTs) {
      const receivedMs = typeof receivedTs === 'number' && receivedTs < 10000000000
        ? receivedTs * 1000
        : new Date(receivedTs).getTime();

      const assetType = typeMap[asset.assetTypeId] || asset.type;
      const offlineHours = assetType?.offlineThreshold || CONNECTION_LOST_HOURS;
      const thresholdMs = offlineHours * 60 * 60 * 1000;

      if (now - receivedMs > thresholdMs) {
        lostConnection.push(asset);
      }
    }
  }

  return { lowBattery, lostConnection };
};

/**
 * Get time since last checkin as a human-readable string
 */
const timeSinceCheckin = (timestamp) => {
  if (!timestamp) return 'Never';
  const ms = typeof timestamp === 'number' && timestamp < 10000000000
    ? timestamp * 1000
    : new Date(timestamp).getTime();
  const diffMs = Date.now() - ms;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h`;
  const minutes = Math.floor(diffMs / (1000 * 60));
  return `${minutes}m`;
};

const exportToCsv = (rows, filename) => {
  if (rows.length === 0) return;

  const headers = [
    'Unit ID', 'Type', 'Subtype', 'Anomaly', 'Battery',
    'Time Since Checkin', 'Last Checked', 'UUID', 'Latitude', 'Longitude',
  ];

  const csvRows = rows.map((asset) => {
    const coord = asset.coordinate || asset.location || {};
    const lat = coord.latitude ?? coord.lat ?? '';
    const lng = coord.longitude ?? coord.lng ?? '';
    const battery = asset.battery ?? asset.beaconBattery ?? '';
    const lastTs = asset.receivedTs || asset.eventTs || '';
    return [
      asset.unitNumber || '',
      asset.type?.name || '',
      asset.subType?.name || '',
      asset._anomalyType || '',
      battery,
      timeSinceCheckin(lastTs),
      formatDateTime(lastTs),
      asset.uuid || '',
      lat,
      lng,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
  });

  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'anomaly-report.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const AnomalyDrawer = ({ open, onClose, assets = [], assetTypes = [] }) => {
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const anomalies = useMemo(() => detectAnomalies(assets, assetTypes), [assets, assetTypes]);

  const displayRows = useMemo(() => {
    const tagged = [];
    if (filter === 'all' || filter === 'battery') {
      for (const a of anomalies.lowBattery) {
        tagged.push({ ...a, _anomalyType: 'Low Battery' });
      }
    }
    if (filter === 'all' || filter === 'connection') {
      for (const a of anomalies.lostConnection) {
        // Avoid duplicates
        if (!tagged.find((t) => t.id === a.id)) {
          tagged.push({ ...a, _anomalyType: 'Lost Connection' });
        }
      }
    }
    return tagged;
  }, [anomalies, filter]);

  const paginatedRows = displayRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleFilterChange = (_, newFilter) => {
    if (newFilter !== null) {
      setFilter(newFilter);
      setPage(0);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          maxHeight: '50vh',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Anomaly Reports
            </Typography>
            <Chip
              icon={<BatteryIcon />}
              label={`${anomalies.lowBattery.length} Low Battery`}
              size="small"
              color="warning"
              variant={filter === 'battery' ? 'filled' : 'outlined'}
            />
            <Chip
              icon={<SignalIcon />}
              label={`${anomalies.lostConnection.length} Lost Connection`}
              size="small"
              color="error"
              variant={filter === 'connection' ? 'filled' : 'outlined'}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size="small"
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="battery">
                <BatteryIcon fontSize="small" sx={{ mr: 1 }} /> Battery
              </ToggleButton>
              <ToggleButton value="connection">
                <SignalIcon fontSize="small" sx={{ mr: 1 }} /> Connection
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Export to CSV">
              <IconButton
                onClick={() => exportToCsv(displayRows, 'anomaly-report.csv')}
                disabled={displayRows.length === 0}
              >
                <ExportIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 'calc(50vh - 140px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Unit ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Subtype</TableCell>
                <TableCell>Anomaly</TableCell>
                <TableCell>Battery</TableCell>
                <TableCell>Time Since Checkin</TableCell>
                <TableCell>Last Checked</TableCell>
                <TableCell>UUID</TableCell>
                <TableCell>Lat</TableCell>
                <TableCell>Long</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No anomalies detected
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRows.map((asset) => {
                  const coord = asset.coordinate || asset.location || {};
                  const lat = coord.latitude ?? coord.lat;
                  const lng = coord.longitude ?? coord.lng;
                  const lastTs = asset.receivedTs || asset.eventTs;
                  return (
                    <TableRow key={`${asset.id}-${asset._anomalyType}`} hover>
                      <TableCell>{asset.unitNumber || '-'}</TableCell>
                      <TableCell>{asset.type?.name || '-'}</TableCell>
                      <TableCell>{asset.subType?.name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={asset._anomalyType}
                          size="small"
                          color={asset._anomalyType === 'Low Battery' ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{asset.battery ?? asset.beaconBattery ?? '-'}</TableCell>
                      <TableCell>{timeSinceCheckin(lastTs)}</TableCell>
                      <TableCell>{formatDateTime(lastTs)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {asset.uuid ? `${asset.uuid.substring(0, 12)}...` : '-'}
                      </TableCell>
                      <TableCell>{lat?.toFixed(6) ?? '-'}</TableCell>
                      <TableCell>{lng?.toFixed(6) ?? '-'}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={displayRows.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[8, 25, 50]}
        />
      </Box>
    </Drawer>
  );
};

export default AnomalyDrawer;
